import { safeFetch } from "@/api/safeFetch";
import { ButtonSiguiente, Input, Select } from "@/components/ui";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const InsertarProfesiones: React.FC = () => {
  const [secciones, setSecciones] = useState([]);
  const [idSeccion, setIdSeccion] = useState("");

  const [nombreProfesion, setNombreProfesion] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [foto, setFoto] = useState<File | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [codigoAcceso, setCodigoAcceso] = useState("");

  // Cargar secciones para seleccionarlas
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const load = async () => {
      try {
        const res = await safeFetch("/api/seccion", { signal });
        const data = await res.json();
        setSecciones(data);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(err.message || "Error al cargar secciones");
        }
      }
    };

    load();

    return () => controller.abort();
  }, []);


  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setFoto(file);
    if (file) setPreview(URL.createObjectURL(file));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!idSeccion || !nombreProfesion || !descripcion || !foto) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("id_seccion_empresa", idSeccion);
      formData.append("nombre_profesion", nombreProfesion);
      formData.append("descripcion", descripcion);
      formData.append("imagen", foto);
      formData.append("codigo_acceso", codigoAcceso);

      const res = await safeFetch("/api/insertarProfesiones", {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Error al insertar profesión");

      setSuccess("Profesión creada correctamente 🎉");

      setNombreProfesion("");
      setDescripcion("");
      setFoto(null);
      setPreview(null);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);


  useEffect(() => {
    return () => {
      // limpiar preview blob
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }

      // limpiar input file
      const inputs = document.querySelectorAll<HTMLInputElement>('input[type="file"]');
      inputs.forEach(input => (input.value = ""));

      // limpiar estados
      setSecciones([]);
      setIdSeccion("");
      setNombreProfesion("");
      setDescripcion("");
      setFoto(null);
      setPreview(null);
      setError("");
      setSuccess("");
      setLoading(false);
    };
  }, []);


  return (
    <div className="w-full h-screen flex items-center justify-center bg-zinc-500 relative px-4 ">

      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/backoffice")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

      <div className="w-full dark:text-zinc-700 max-w-2xl p-6 bg-zinc-400 shadow-md rounded-xl shadow-yellow-600 border-zinc-700 border-r-4 border-t-2">

        <h2 className="text-2xl font-bold mb-4 text-center">
          Insertar Nueva Profesión
        </h2>

        {error && <p className="text-red-600">{error}</p>}
        {success && <p className="text-green-600">{success}</p>}

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">

          {/* Seleccionar sección */}
          <div>
            <label className="font-medium">Sección Empresa</label>
            <Select
              className="w-full dark:bg-white"
              value={idSeccion}
              onChange={(e) => setIdSeccion(e.target.value)}
              placeholder="-- Seleccionar --"
              options={secciones.map((s: any) => ({
                value: s.id,
                label: s.nombreSeccion
              }))}
            />

          </div>

          {/* Nombre profesión */}
          <div>
            <label className="font-medium">Nombre de la Profesión</label>
            <Input
              type="text"
              value={nombreProfesion}
              className="w-full border p-2 rounded mt-1"
              onChange={(e) => setNombreProfesion(e.target.value)}
            />
          </div>

          {/* Código de acceso */}
          <div>
            <label className="font-medium">Código de acceso (opcional)</label>
            <Input
              type="text"
              value={codigoAcceso}
              className="w-full border p-2 rounded mt-1"
              onChange={(e) => setCodigoAcceso(e.target.value)}
              placeholder="Ej: ADMIN-2026"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="font-medium">Descripción</label>
            <textarea
              className="w-full border p-2 rounded mt-1 dark:bg-white"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>

          {/* Imagen */}
          <div className="flex items-center justify-between gap-6">
            <div className="flex-1">
              <label className="font-medium mb-2">Imagen</label>

              <label className="flex items-center justify-center py-2 h-8 w-3/6 bg-zinc-600 text-white rounded-md cursor-pointer hover:bg-zinc-700 border shadow-md shadow-zinc-700">
                Seleccionar Imagen
                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </label>

              {foto && <span className="text-sm text-gray-600">{foto.name}</span>}
            </div>

            {preview && (
              <img src={preview} className="w-20 h-20 object-cover rounded-lg shadow-md" />
            )}
          </div>

          <ButtonSiguiente
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            {loading ? "Guardando..." : "Guardar Profesión"}
          </ButtonSiguiente>

        </form>

      </div>
    </div>
  );
};

export default InsertarProfesiones;
